import { Link } from "react-router-dom";
import {
  Sparkles,
  Radio,
  Users,
  MessageSquare,
  Gift,
  TrendingUp,
  Heart,
  ArrowRight,
  Coins,
} from "lucide-react";

/**
 * Public /about page — SEO-friendly marketing copy for MyPandaX. Written
 * for Google search snippets + first-time visitors landing from an ad.
 * Kept as a single scrollable long-form page so the copy indexes cleanly.
 */
const CORE_FEATURES = [
  {
    icon: Radio,
    title: "Live Streaming",
    body: "Go live instantly. Host solo broadcasts or bring co-hosts on stage. Interact with your audience in real time — chat, hearts, and gift animations right on screen.",
  },
  {
    icon: Users,
    title: "Private Communities",
    body: "Create exclusive groups with customisable roles, memberships, and privacy settings. Build spaces just for your fans, friends, or paying subscribers.",
  },
  {
    icon: MessageSquare,
    title: "Real-Time Messaging",
    body: "Direct messages, group chats, voice conversations, and community discussions — stay connected with the people who matter.",
  },
  {
    icon: Gift,
    title: "Virtual Gifts & Creator Support",
    body: "Fans send virtual gifts and tips during live streams and community events. Creators earn coins that convert to real money.",
  },
  {
    icon: TrendingUp,
    title: "Creator Growth Tools",
    body: "Build your audience, promote content, earn revenue, and engage supporters with a suite of powerful creator-focused features.",
  },
];

const COMMON_USES = [
  "Live entertainment and interactive broadcasts",
  "Creator and fan engagement",
  "Private VIP communities",
  "Social networking and friendships",
  "Business promotions and product showcases",
  "Gaming streams and watch parties",
  "Educational sessions and workshops",
  "Fitness, lifestyle, music, and special-interest communities",
];

export default function About() {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="about-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center pb-12 border-b border-white/10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-rose-400 mb-4">
            <Sparkles size={12} /> About MyPandaX · Adults only (18+)
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Live streams,
            <br />
            real communities,
            <br />
            <span className="text-rose-400">authentic</span> connections.
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 mt-6 max-w-xl mx-auto leading-relaxed">
            MyPandaX is an <strong className="text-white">18+ social entertainment platform</strong> where
            people connect through live video streaming, private communities, real-time
            messaging, and interactive virtual gifting. Whether you're a creator,
            performer, influencer, business owner, or simply looking to meet new people —
            MyPandaX is a safe, engaging space to build meaningful connections and grow
            your community.
          </p>
          <p className="text-xs text-zinc-500 mt-4 max-w-xl mx-auto leading-relaxed">
            MyPandaX is an adults-only (18+) membership community. Nudity and sexually
            explicit content are not permitted anywhere on the platform. You must be 18
            years of age or older to sign up.
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              data-testid="about-cta-signup"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white font-semibold rounded-full px-8 py-3 text-sm"
            >
              Join free <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Core features */}
        <section className="py-12">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-6">
            Core features
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CORE_FEATURES.map((f) => (
              <div
                key={f.title}
                data-testid={`about-feature-${f.title.split(" ")[0].toLowerCase()}`}
                className="bg-zinc-950/60 border border-white/10 rounded-md p-4 hover:border-rose-500/40 transition-colors"
              >
                <div className="flex items-center gap-2 text-rose-300 mb-2">
                  <f.icon size={16} />
                  <div className="text-sm font-semibold text-white">{f.title}</div>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Common uses */}
        <section className="py-12 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-6">
            Common uses
          </div>
          <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
            {COMMON_USES.map((use) => (
              <li
                key={use}
                className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 shrink-0" />
                {use}
              </li>
            ))}
          </ul>
        </section>

        {/* Why choose */}
        <section className="py-12 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-6">
            Why choose MyPandaX?
          </div>
          <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
            <p>
              MyPandaX is designed to bring people together through{" "}
              <b className="text-white">authentic interactions, creativity, and community</b>. We
              believe everyone deserves a platform where they can express themselves, build
              lasting relationships, and be rewarded for creating engaging content.
            </p>
            <p>
              Whether you're streaming for fun, growing a brand, or building a loyal fanbase,
              MyPandaX gives you the tools to connect, create, and thrive.
            </p>
          </div>
        </section>

        {/* Cost + creator split */}
        <section className="py-12 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-6">
            Cost & creator earnings
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div
              className="bg-zinc-950/60 border border-white/10 rounded-md p-5"
              data-testid="about-cost-card"
            >
              <div className="flex items-center gap-2 text-emerald-300 mb-2">
                <Heart size={16} />
                <div className="text-sm font-semibold text-white">Free to join</div>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                No mandatory subscriptions to create an account, browse public content,
                join communities, or interact with creators. Optional premium features,
                memberships, and virtual purchases are available for enhanced experiences
                or to support creators directly.
              </p>
            </div>
            <div
              className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-amber-400/30 rounded-md p-5"
              data-testid="about-creator-split-card"
            >
              <div className="flex items-center gap-2 text-amber-300 mb-2">
                <Coins size={16} />
                <div className="text-sm font-semibold text-white">Creators keep 70%</div>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                MyPandaX operates a{" "}
                <b className="text-amber-200">70 / 30 revenue split</b> in favour of
                creators — you keep <b className="text-white">70%</b> of every coin gifted
                to you on live streams and inside private communities. The remaining 30%
                covers platform operations and safety infrastructure.
              </p>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="pt-8 pb-4 text-center border-t border-white/10">
          <p className="text-sm text-zinc-400 mb-4">
            Ready to go live, connect, and get supported?
          </p>
          <Link
            to="/register"
            data-testid="about-cta-footer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white font-semibold rounded-full px-8 py-3 text-sm"
          >
            Create your free account <ArrowRight size={14} />
          </Link>
          <p className="text-[11px] text-zinc-600 mt-6 flex items-center justify-center gap-1">
            <Heart size={10} className="text-rose-500" /> Built for creators, communities,
            and everyone in between.
          </p>
        </section>
      </div>
    </div>
  );
}
